import type { ChangeEvent } from 'react';
import moment from 'moment';
import { isEqual, uniqBy } from 'lodash-es';
import type { ColumnFiltersState } from '@tanstack/react-table';

import type { DataTableConfig, TableState } from 'components/data-table/types';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { SERVER_DATE_FORMAT } from 'utils/dates';
import { formatNumber } from 'utils/number';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import wellStatusTypes from 'enums/well_status_types.json';
import type { PackageSeller } from 'types/api-responses';
import type { FeatureFlagWindow } from 'types/window';

import { EMPTY_ASSET_LIST } from './asset-list-utils';
import {
  EMPTY_MAP_QUERY_FILTERS,
  mapQueryFilterParams,
} from './steps/map-query-source/utils';
import type { MapQueryFiltersUpdate } from './steps/map-query-source/types';
import type {
  AcquiringEntity,
  AssetSelectionFilters,
  CreateDraftPayload,
  DraftAssetRow,
  DraftRefusalBody,
  DraftAssetsQueryParams,
  DraftResponse,
  DraftSlot,
  DraftSlotsQueryParams,
  DraftStatusChangeEvent,
  EditPackageDetail,
  EntityRef,
  EntitySearchParams,
  FlatSlotRow,
  InterestBreakdown,
  InterestBreakdownVerification,
  NamedAssetListEntity,
  PackageAcquirerEntity,
  PackageFormInstance,
  PackageFormValues,
  PackageSearchParams,
  PackageWizardMode,
  SavePackagePayload,
  ScopePayload,
  SlotSelectionFilters,
  SourceRow,
  SourceSelectionChange,
  SourceType,
  Step2SlotRow,
  UpdateDraftPayload,
  VerificationState,
  VerificationType,
} from './types';
import { mergeSourceSelection } from './source-selection';

declare let window: FeatureFlagWindow;

/**
 * Project a wire-shape `PackageAcquirerEntity` (from `/queries/packages`
 * or `GET /packages/:id`) onto the form-shape `AcquiringEntity`. Coerces
 * `null` classification/type to `undefined` so the form's optional-field
 * semantics roundtrip cleanly. Used by both the Step 0 package-source
 * pre-population and the Edit-mode hydration so the picker grid
 * populates identically in both flows.
 *
 * @param {PackageAcquirerEntity} e - Wire shape from the package response
 * @return {AcquiringEntity} Form-shape acquirer
 */
export const packageAcquirerToFormEntity = (
  e: PackageAcquirerEntity
): AcquiringEntity => ({
  id: e.id,
  name: e.name,
  entityClassification: e.entityClassification ?? undefined,
  entityType: e.entityType ?? undefined,
  crmTractCount: e.crmTractCount,
  titleAssetCount: e.titleAssetCount,
  crmAccountCount: e.crmAccountCount,
});

// ─────────────────────────────────────
// Initial Form State
// ─────────────────────────────────────

/**
 * Carbon dropdown options for the slot table's "Well Status" column,
 * sorted alphabetically by label so the order isn't tied to the json's
 * authoring order. Shared by `slot-table.tsx` (Step 1 selectable view)
 * and `step2-slot-details-table.tsx` (Step 2 read-only view).
 */
export const WELL_STATUS_OPTIONS = Object.entries(wellStatusTypes)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** Default form values for create mode */
export const INITIAL_FORM: PackageFormValues = {
  // Step 0
  sourceType: null,
  sources: [],
  assetList: EMPTY_ASSET_LIST,
  mapQuery: EMPTY_MAP_QUERY_FILTERS,
  asOfDate: null,
  draftId: null,

  // Step 1 — the wizard's LTD population is derived from this.
  typeCurveScenarioId: null,

  // Step 2
  packageName: '',
  acquiringEntities: [],
  effectiveDate: null,
  psaDate: null,
  closeDate: null,
};

/**
 * Whether creating a package offers the Map Query source. It is behind
 * `FEATURE_FLAG_PACKAGE_MAP_QUERY` until the tract index carries every Tract's
 * state and county.
 *
 * @return {boolean} True when the flag is on
 */
export const mapQueryOffered = (): boolean =>
  window.CONFIG?.featureFlags?.packageMapQuery === true;

/**
 * The form a new package opens with: on Map Query while it is offered, else on
 * no source.
 *
 * @return {PackageFormValues} Create-mode defaults
 */
export const createFormDefaults = (): PackageFormValues => ({
  ...INITIAL_FORM,
  sourceType: mapQueryOffered() ? 'mapQuery' : null,
});

/** One source a new package can be created from, as the dropdown offers it. */
export interface CreateSourceOption {
  id: Extract<SourceType, 'mapQuery' | 'assetList' | 'package'>;
  label: string;
  description: string;
  /** A caveat shown under the description. */
  note?: string;
}

/** What an Existing Package's As of Date can change, in its tooltip entry and its warning. */
export const CLONE_NOTE =
  'Selecting an As of Date may change which assets are included in the package because assets were added or removed since that date.';

const CREATE_SOURCE_OPTIONS: CreateSourceOption[] = [
  {
    id: 'mapQuery',
    label: 'Map Query',
    description:
      'Find tracts on a map — filter by entity and location, or search the visible map extent or a polygon you draw',
  },
  {
    id: 'assetList',
    label: 'Asset List',
    // U+2011 non-breaking hyphens keep "T-xxxxxxxx" whole when the text wraps.
    description:
      'Paste or upload a list of Asset Names (T‑xxxxxxxx or ST‑xxxxxxxxx)',
  },
  {
    id: 'package',
    label: 'Existing Package',
    description:
      'Clone or combine existing packages as of a date, keeping their exclusions',
    note: CLONE_NOTE,
  },
];

/**
 * The sources the "Create Package from" dropdown offers, in order: Map Query
 * (while offered), Asset List, Existing Package.
 *
 * @return {CreateSourceOption[]} The options
 */
export const createSourceOptions = (): CreateSourceOption[] =>
  mapQueryOffered()
    ? CREATE_SOURCE_OPTIONS
    : CREATE_SOURCE_OPTIONS.filter((option) => option.id !== 'mapQuery');

/**
 * The As of Date a switch of source leaves when creating: Existing Package
 * defaults to today, Map Query has no default, and the asset list keeps the date
 * it had.
 *
 * @param {SourceType} next - The source switched to
 * @param {string | null} current - The As of Date before the switch
 * @return {string | null} The As of Date after it ('YYYY-MM-DD')
 */
export const asOfDateAfterSourceSwitch = (
  next: SourceType,
  current: string | null
): string | null => {
  if (next === 'package') return moment().format(SERVER_DATE_FORMAT);
  if (next === 'mapQuery') return null;
  return current;
};

// ─────────────────────────────────────
// Step gating helpers
// ─────────────────────────────────────

/** What Step 0's gate needs to know about the asset list beyond the form. */
export interface AssetListReadiness {
  /** Matched names in the working list: the Asset List source, or the list beside a primary. */
  matched: number;
  /**
   * Every name of the list has an answer and no file is uploading. A failed
   * check leaves names unanswered, so it holds Next until a check succeeds or
   * those names leave the list.
   */
  settled: boolean;
}

/**
 * Step 0 advance predicate. At least one source and an `asOfDate` must be
 * set — that is the scope the Next-click POSTs or PATCHes. It holds after the
 * first sync too: Next skips the sync on an empty selection, so letting it
 * advance would carry the draft's old scope forward while the header shows
 * none.
 *
 * On the asset list the source is the matched names, and the list must be
 * settled: a name still being checked might match, and after a failed check
 * the matched set is not known. The list beside Map Query or Existing Package
 * must be settled too, since Next sends it.
 *
 * Map Query needs at least one ticked entity (its sellers), even with a list,
 * and something to deliver: results for the current filters, or a matched
 * name. Existing Package needs a package: a list alone does not make a clone.
 *
 * The one exception is an edit of a package with no entity-backed seller:
 * hydration leaves no tile picked and nothing selected, and the bootstrap
 * draft already holds the package's scope. That is the state the user
 * arrived in, not one they made, so it advances; picking a tile ends it.
 *
 * @param {PackageFormValues} v - Current form values
 * @param {AssetListReadiness} assetList - The asset list's check state
 * @param {number | null} mapQueryTotal - The Map Query results' total for the
 *   current filters, null until their search answers
 * @return {boolean} True when Step 0's Next button should be enabled
 */
export const canAdvanceStep0 = (
  v: PackageFormValues,
  assetList: AssetListReadiness,
  mapQueryTotal: number | null = null
): boolean => {
  if (v.sourceType === 'assetList') {
    return v.asOfDate !== null && assetList.settled && assetList.matched > 0;
  }
  if (v.sourceType === 'mapQuery') {
    return (
      v.asOfDate !== null &&
      v.sources.length > 0 &&
      assetList.settled &&
      ((mapQueryTotal ?? 0) > 0 || assetList.matched > 0)
    );
  }
  const listSettled = v.sourceType !== 'package' || assetList.settled;
  return (
    (v.sources.length > 0 && v.asOfDate !== null && listSettled) ||
    (v.draftId !== null && v.sourceType === null)
  );
};

/**
 * What the wizard is doing, with no per-selection detail. This is the
 * breadcrumb's current-page label: a breadcrumb names a place, and a place
 * that renames itself every time the user ticks a source row isn't one.
 *
 * @param {PackageWizardMode} mode - Create or edit
 * @return {string} Breadcrumb label
 */
export const wizardBreadcrumbLabel = (mode: PackageWizardMode): string => {
  return mode === 'edit' ? 'Edit Package' : 'Create Package';
};

/**
 * Wizard header title. Counts the Step 0 selection so the header answers
 * "how many sources is this package being built from" at every step — Step 1
 * and Step 2 don't show the source tables. No count until something is
 * selected, so the title reads plainly on first paint.
 *
 * @param {PackageWizardMode} mode - Create or edit
 * @param {number} sourceCount - How many Step 0 sources are selected
 * @return {string} Header title
 */
export const wizardTitle = (
  mode: PackageWizardMode,
  sourceCount: number
): string => {
  const base = wizardBreadcrumbLabel(mode);
  if (sourceCount === 0) return base;

  return `${base} (${formatNumber({
    value: sourceCount,
    minPrecision: 0,
  })})`;
};

/**
 * A count with thousands separators.
 *
 * @param {number} value - The count
 * @return {string} It, formatted
 */
export const formatCount = (value: number): string =>
  formatNumber({ value, minPrecision: 0 });

/**
 * A count and its noun, singular at one.
 *
 * @param {number} value - The count
 * @param {string} one - The noun for one
 * @param {string} many - The noun for any other count
 * @return {string} "1 asset", "2 assets"
 */
export const counted = (value: number, one: string, many: string): string =>
  `${formatCount(value)} ${value === 1 ? one : many}`;

/**
 * Names as a short label: the one name, or the first and how many more
 * ("Project Timberwolf +1").
 *
 * @param {string[]} names - The names, in order
 * @return {string} The label, empty for no names
 */
export const collapseNames = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names[0]} +${formatCount(names.length - 1)}`;

/**
 * Names as a list in a sentence: "A", "A and B", "A, B and C".
 *
 * @param {string[]} names - The names, in order
 * @return {string} The list, empty for no names
 */
export const listNames = (names: string[]): string =>
  names.length <= 2
    ? names.join(' and ')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * The name the Summary step's map banner gives the package's source: the
 * entities or source packages picked, or on the asset list its Tracts' owners,
 * as one label ("Project Timberwolf +1"); a dash when there are none.
 *
 * @param {SourceType | null} sourceType - Which source was picked
 * @param {SourceRow[]} sources - The picked entities or packages
 * @param {NamedAssetListEntity[]} listEntities - The asset list's owners
 * @return {string} The label
 */
export const sourceBannerName = (
  sourceType: SourceType | null,
  sources: SourceRow[],
  listEntities: NamedAssetListEntity[]
): string =>
  collapseNames(
    (sourceType === 'assetList' ? listEntities : sources).map(
      ({ name }) => name
    )
  ) || '—';

/**
 * Why the header's source tags cannot be removed, or undefined when they can.
 *
 * Two things fix the selection. Off the Select Source step, the draft's assets
 * and slots have already been materialized from these sources and nothing on
 * the later steps rebuilds them — a removal there would drop the tag and the
 * count while the assets stayed as they were, so the header would contradict
 * the step below it. On the Select Source step, an edit down to its last
 * seller: `editPackageFragments` omits the key on an empty list so a PATCH can
 * never wipe the set, so the removal would not survive the save.
 *
 * Creating a package with one source keeps its close button — clearing the
 * selection there writes nothing, and Next is disabled at zero anyway.
 *
 * @param {PackageWizardMode} mode - Create or edit
 * @param {boolean} onSourceStep - Whether Select Source is the step showing
 * @param {number} sourceCount - How many sources are selected
 * @return {string | undefined} The reason, or undefined when removal is offered
 */
export const sourceLockLabel = (
  mode: PackageWizardMode,
  onSourceStep: boolean,
  sourceCount: number
): string | undefined => {
  if (!onSourceStep) {
    return 'Sources are chosen on the Select Source step. Go back there to change them.';
  }
  if (mode === 'edit' && sourceCount === 1) {
    return 'A package keeps at least one seller. Add another before removing this one.';
  }
  return undefined;
};

/**
 * Step 2 advance predicate. Mirrors the BE's `:wizard` validations:
 * only `name` non-blank is required. The "at least one asset" rule is
 * gated upstream on Step 1, and dates / acquiring entities are
 * nullable on the wire — the wizard collects them when set, omits
 * them when not.
 *
 * @param {PackageFormValues} v - Current form values
 * @return {boolean} True when Step 2's Save/Create button should be enabled
 */
export const canAdvanceStep2 = (v: PackageFormValues): boolean =>
  v.packageName.trim() !== '';

/**
 * Names of the 4 date fields on `PackageFormValues`. Enumerated so
 * {@link makeDateFieldHandler} stays type-safe and can't be pointed at an
 * arbitrary form field.
 */
type FormDateField = Extract<
  keyof PackageFormValues,
  'asOfDate' | 'effectiveDate' | 'psaDate' | 'closeDate'
>;

/**
 * Build a Carbon DatePicker `onChange` handler that writes `yyyy-MM-dd` (or
 * null when cleared) to the given form date field. Idempotent — skips the
 * write when the next value equals `current`, so Carbon's repeat
 * `onChange` fires on reopen don't cause redundant store notifications.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {FormDateField} field - Target date field on form values
 * @param {string | null} current - Current form value, for dedup
 * @param {Function} [onIgnored] - Called for a date the field cannot show
 *   back ({@link pickedDate}), so the caller can show the kept value again
 * @return {Function} DatePicker `onChange` handler
 */
export const makeDateFieldHandler =
  (
    form: PackageFormInstance,
    field: FormDateField,
    current: string | null,
    onIgnored?: () => void
  ) =>
  (dates: Date[]) => {
    const next = pickedDate(dates);
    if (next === undefined) {
      onIgnored?.();
      return;
    }
    if (next === current) return;
    form.setFieldValue(field, next);
  };

/**
 * Build a Carbon DatePicker `onInputChange` handler that clears the form date
 * field when its text is emptied. flatpickr emits no `onChange` for text
 * cleared by typing, so the form would otherwise keep a date the field no
 * longer shows.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {FormDateField} field - Target date field on form values
 * @param {string | null} current - Current form value, for dedup
 * @param {Function} [onCleared] - Called once the field is cleared
 * @return {Function} DatePicker `onInputChange` handler
 */
export const makeDateClearHandler =
  (
    form: PackageFormInstance,
    field: FormDateField,
    current: string | null,
    onCleared?: () => void
  ) =>
  ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (target.value !== '' || current === null) return;
    form.setFieldValue(field, null);
    onCleared?.();
  };

/**
 * The `yyyy-MM-dd` a Carbon DatePicker `onChange` carries, null when cleared,
 * or undefined for a date the field cannot show back. Typing over a date can
 * leave text such as 11/02/20260, which the picker reads as the year 20260;
 * the field formats its value from `yyyy-MM-dd` and throws on that, taking the
 * page down, so the caller keeps the value it had and re-mounts the field to
 * show it again.
 *
 * @param {Date[]} dates - The picker's selected dates
 * @return {string | null | undefined} The date, null, or undefined to ignore
 */
export const pickedDate = (dates: Date[]): string | null | undefined => {
  if (!dates[0]) return null;
  const date = moment(dates[0]).format('YYYY-MM-DD');
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined;
};

/**
 * Translate TanStack `ColumnFiltersState` into the entity-search payload.
 * Seeds all four filter keys to `undefined` first so a missing entry in
 * `filters` (TanStack drops cleared filters) produces an explicit clear
 * when spread into the previous payload. `JSON.stringify` strips
 * `undefined`-valued keys on the wire (see `lib/request`), so the BE
 * sees no constraint for cleared filters.
 *
 * @param {ColumnFiltersState} filters - Current TanStack column filter state
 * @return {Partial<EntitySearchParams>} Patch suitable for `setSearchPayload`
 */
export function entityFilterStateToParams(
  filters: ColumnFiltersState
): Partial<EntitySearchParams> {
  const patch: Partial<EntitySearchParams> = {
    entityClassifications: undefined,
    entityTypes: undefined,
    createdAt: undefined,
    updatedAt: undefined,
  };
  for (const filter of filters) {
    switch (filter.id) {
      case 'entityClassification': {
        const values = (filter.value as string[]) ?? [];
        if (values.length > 0) patch.entityClassifications = values;
        break;
      }
      case 'entityType': {
        const values = (filter.value as string[]) ?? [];
        if (values.length > 0) patch.entityTypes = values;
        break;
      }
      case 'createdAt': {
        const [start, end] = (filter.value as [
          string | null,
          string | null,
        ]) ?? [null, null];
        if (start || end) {
          patch.createdAt = {
            start: start ?? undefined,
            end: end ?? undefined,
          };
        }
        break;
      }
      case 'updatedAt': {
        const [start, end] = (filter.value as [
          string | null,
          string | null,
        ]) ?? [null, null];
        if (start || end) {
          patch.updatedAt = {
            start: start ?? undefined,
            end: end ?? undefined,
          };
        }
        break;
      }
    }
  }
  return patch;
}

/**
 * Translate TanStack `ColumnFiltersState` into the package-search payload.
 * Date ranges are flattened to `*Start` / `*End` keys (BE permits
 * `created_at_start` / `created_at_end` etc., not the nested `{start,end}`
 * shape used by entities). `containsInventoryAssets` is bound to a
 * checkbox-group with a single `'true'` option — presence in the array
 * sets the boolean. Seeds all six keys so cleared filters spread as
 * `undefined` (stripped on the wire by `JSON.stringify`).
 *
 * @param {ColumnFiltersState} filters - Current TanStack column filter state
 * @return {Partial<PackageSearchParams>} Patch suitable for `setSearchPayload`
 */
/**
 * Flatten a `DraftSlotsResponse.slots` array into one row per `(slot, tract)`
 * pair, repeating slot-level scalars across the slot's tracts. Slot's own
 * `selected` / `nri` / `workingInterest` are renamed `slotSelected` /
 * `slotNri` / `slotWorkingInterest` so they don't collide with the tract
 * values of the same names. Composite `rowId` (`${slotId}__${assetId}`) is
 * unique within a page (BE invariant — a tract appears under at most one
 * slot per page).
 *
 * @param {DraftSlot[]} slots - Slot groups from the slots endpoint
 * @return {FlatSlotRow[]} One row per tract within each slot
 */
export function flattenDraftSlots(slots: DraftSlot[]): FlatSlotRow[] {
  return slots.flatMap((slot) =>
    slot.tracts.map<FlatSlotRow>((tract) => ({
      rowId: `${slot.slotId}__${tract.assetId}`,

      slotId: slot.slotId,
      unitName: slot.unitName,
      unitHref: slot.unitHref,
      unitTags: slot.unitTags,
      formation: slot.formation,
      slotNumber: slot.slotNumber,
      wellName: slot.wellName,
      uwi: slot.uwi,
      operator: slot.operator,
      wellStatus: slot.wellStatus,
      reserveCategory: slot.reserveCategory,
      forecastStatus: slot.forecastStatus,
      volumeSource: slot.volumeSource,
      wellProducingLength: slot.wellProducingLength,
      wellDepth: slot.wellDepth,
      wellFirstProductionDate: slot.wellFirstProductionDate,
      wellSpudDate: slot.wellSpudDate,
      wellPermitDate: slot.wellPermitDate,
      holeDirection: slot.holeDirection,
      slotSelected: slot.selected,
      slotNri: slot.nri,
      slotWorkingInterest: slot.workingInterest,
      wiSource: slot.wiSource ?? undefined,

      assetId: tract.assetId,
      crmTractTags: tract.crmTractTags,
      assetType: tract.assetType,
      assetName: tract.assetName,
      subtractCount: tract.subtractCount ?? null,
      includedSubtractCount: tract.includedSubtractCount ?? null,
      selected: tract.selected,
      crmAccountName: tract.crmAccountName,
      instrumentType: tract.instrumentType,
      interestType: tract.interestType,
      tractNetAcres: tract.tractNetAcres,
      netAcreageUnits: tract.netAcreageUnits,
      tractGrossAcres: tract.tractGrossAcres,
      surveyArea: tract.surveyArea,
      tractQuarterCall: tract.tractQuarterCall,
      startDepth: tract.startDepth,
      endDepth: tract.endDepth,
      allocationMethod: tract.allocationMethod,
      nri: tract.nri,
      workingInterest: tract.workingInterest,

      subtracts: tract.interestBreakdown,
    }))
  );
}

/**
 * Translate TanStack `ColumnFiltersState` into the draft-slots payload.
 * Covers every BE array filter wired with inline UI today: enum-typed
 * (`wellStatus` — multiselect emits string[]) and text-typed (`slotId`,
 * `assetName`, `unitName`, `operator`, `formation` — text inputs emit a
 * single string, wrapped in `[value]` since the BE expects `WHERE col IN
 * (values)`). DataTable's filter UIs both clear to `undefined` on blank,
 * so non-empty values are guaranteed by the time we iterate here.
 * `reserveCategory` / `forecastStatus` / `volumeSource` are BE-supported
 * but their columns aren't visible yet; the seed entries keep the patch
 * shape stable for when they're wired. Seeds every handled key to
 * `undefined` so a cleared filter spreads as an explicit clear into the
 * previous payload.
 *
 * @param {ColumnFiltersState} filters - Current TanStack column filter state
 * @return {Partial<DraftSlotsQueryParams>} Patch suitable for `setSearchPayload`
 */
export function draftSlotFilterStateToParams(
  filters: ColumnFiltersState
): Partial<DraftSlotsQueryParams> {
  const patch: Partial<DraftSlotsQueryParams> = {
    wellStatus: undefined,
    reserveCategory: undefined,
    forecastStatus: undefined,
    volumeSource: undefined,
    slotId: undefined,
    assetName: undefined,
    unitName: undefined,
    operator: undefined,
    formation: undefined,
  };
  for (const filter of filters) {
    switch (filter.id) {
      case 'wellStatus':
        patch.wellStatus = filter.value as string[];
        break;
      case 'reserveCategory':
        patch.reserveCategory = filter.value as string[];
        break;
      case 'forecastStatus':
        patch.forecastStatus = filter.value as string[];
        break;
      case 'volumeSource':
        patch.volumeSource = filter.value as string[];
        break;
      case 'slotId':
        patch.slotId = [filter.value as string];
        break;
      case 'assetName':
        patch.assetName = [filter.value as string];
        break;
      case 'unitName':
        patch.unitName = [filter.value as string];
        break;
      case 'operator':
        patch.operator = [filter.value as string];
        break;
      case 'formation':
        patch.formation = [filter.value as string];
        break;
    }
  }
  return patch;
}

/**
 * Translate DataTable's `params.sorting` into the BE wire shape both the
 * draft assets and draft slots endpoints accept. Returns an empty object
 * when no primary sort is active so callers can spread it unconditionally
 * without polluting the request with `sortAttribute: undefined`.
 *
 * Only the first sort key is honored — the BE supports a single primary
 * sort plus an internal `_doc` tiebreaker; multi-column sort isn't part
 * of the contract.
 *
 * @param {Array} sorting - DataTable sort state (`TableState['sorting']`)
 * @return {object} BE-shape `{ sortAttribute, sortDirection }` patch, or `{}` when no sort active
 */
export function sortParamsFromState(
  sorting: TableState['sorting']
):
  | { sortAttribute: string; sortDirection: 'ASC' | 'DESC' }
  | Record<string, never> {
  const primary = sorting[0];
  if (!primary) return {};
  return {
    sortAttribute: primary.id,
    sortDirection: primary.desc ? 'DESC' : 'ASC',
  };
}

/**
 * Translate TanStack `ColumnFiltersState` into the draft-assets payload.
 * Covers the BE text filters wired with inline UI today (`asset_name`,
 * `entity_name`, `survey_area`, `quarter_call`, `instrument_type`,
 * `net_acreage_units`). Each is a scalar text permit on the BE — passes
 * the value straight through. Range filters (net_acres, gross_acres)
 * and date filters are left out until a proper range UI lands; columns
 * for those are gated with `enableColumnFilter: false`.
 *
 * @param {ColumnFiltersState} filters - Current TanStack column filter state
 * @return {Partial<DraftAssetsQueryParams>} Patch suitable for the assets fetch
 */
export function draftAssetFilterStateToParams(
  filters: ColumnFiltersState
): Partial<DraftAssetsQueryParams> {
  const patch: Partial<DraftAssetsQueryParams> = {
    assetName: undefined,
    entityName: undefined,
    surveyArea: undefined,
    quarterCall: undefined,
    instrumentType: undefined,
    netAcreageUnits: undefined,
  };
  for (const filter of filters) {
    switch (filter.id) {
      case 'name':
        patch.assetName = filter.value as string;
        break;
      case 'entity':
        patch.entityName = filter.value as string;
        break;
      case 'surveyArea':
        patch.surveyArea = filter.value as string;
        break;
      case 'quarterCall':
        patch.quarterCall = filter.value as string;
        break;
      case 'instrumentType':
        patch.instrumentType = filter.value as string;
        break;
      case 'netAcreageUnits':
        patch.netAcreageUnits = filter.value as string;
        break;
    }
  }
  return patch;
}

/**
 * The draft assets table's search, column filters and sort in the body its
 * query sends, without the page. The Step 1 export sends the same, so the
 * file holds the rows the table shows, in its order.
 *
 * @param {object} state - The table's search, column filters and sort
 * @return {Partial<DraftAssetsQueryParams>} The query body's scope
 */
export function draftAssetsQueryScope({
  globalFilter,
  columnFilters,
  sorting,
}: Pick<
  TableState,
  'globalFilter' | 'columnFilters' | 'sorting'
>): Partial<DraftAssetsQueryParams> {
  return {
    text: globalFilter || undefined,
    ...sortParamsFromState(sorting),
    ...draftAssetFilterStateToParams(columnFilters),
  };
}

/**
 * Asset counterpart to {@link buildSelectionFilters}. Projects only the
 * keys `AssetSelectionFilters` accepts so a `select_all` mutation flips
 * exactly the assets currently in view (across all pages of the same
 * filter), and not anything outside the visible scope. Returns null
 * when no filter is active so the caller can choose true select-all
 * (universe-wide) over filtered select-all.
 *
 * @param {TableState | null} params - Last params DataTable fired
 * @return {AssetSelectionFilters | null} BE-shape filters, or null if none
 */
export function buildAssetSelectionFilters(
  params: TableState | null
): AssetSelectionFilters | null {
  if (params === null) return null;
  const columnFilters = draftAssetFilterStateToParams(params.columnFilters);
  const filters: AssetSelectionFilters = {
    text: params.globalFilter || undefined,
    assetName: columnFilters.assetName,
    entityName: columnFilters.entityName,
    surveyArea: columnFilters.surveyArea,
    quarterCall: columnFilters.quarterCall,
    instrumentType: columnFilters.instrumentType,
    netAcreageUnits: columnFilters.netAcreageUnits,
  };
  const hasAny = Object.values(filters).some((v) => v !== undefined);
  return hasAny ? filters : null;
}

/**
 * Build the BE selection-filter scope from the table's last `TableState`.
 * Mirrors the slot fetch's filter scope so a `select_all_filtered` mutation
 * flips exactly the allocations currently in view (across all pages of the
 * same filter). Returns null when no filter is active so the caller can
 * choose `select_all` (universal) over `select_all_filtered` (scoped).
 *
 * Projects only the keys `SlotSelectionFilters` accepts —
 * `slot_selections_controller.rb#selection_params` permits a strict subset
 * of `GET /slots`'s filter list. Spreading `columnFilters` raw would leak
 * `slotId` / `assetName` (FE-only filters) into the payload and the BE
 * would silently drop them, scoping the bulk-toggle wider than the visible
 * table — destructive on a select-all mutation.
 *
 * @param {TableState | null} params - Last params DataTable fired
 * @return {SlotSelectionFilters | null} BE-shape filters, or null if none
 */
export function buildSelectionFilters(
  params: TableState | null
): SlotSelectionFilters | null {
  if (params === null) return null;
  const columnFilters = draftSlotFilterStateToParams(params.columnFilters);
  const filters: SlotSelectionFilters = {
    text: params.globalFilter || undefined,
    formation: columnFilters.formation,
    unitName: columnFilters.unitName,
    wellStatus: columnFilters.wellStatus,
    reserveCategory: columnFilters.reserveCategory,
    forecastStatus: columnFilters.forecastStatus,
    volumeSource: columnFilters.volumeSource,
    operator: columnFilters.operator,
    assetId: columnFilters.assetId,
  };
  const hasAny = Object.values(filters).some((v) => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined;
  });
  return hasAny ? filters : null;
}

export function packageFilterStateToParams(
  filters: ColumnFiltersState
): Partial<PackageSearchParams> {
  const patch: Partial<PackageSearchParams> = {
    containsInventoryAssets: undefined,
    interestTypes: undefined,
    createdAtStart: undefined,
    createdAtEnd: undefined,
    updatedAtStart: undefined,
    updatedAtEnd: undefined,
  };
  for (const filter of filters) {
    switch (filter.id) {
      case 'containsInventoryAssets': {
        const values = (filter.value as string[]) ?? [];
        if (values.includes('true')) patch.containsInventoryAssets = true;
        break;
      }
      case 'interestTypes': {
        const values = (filter.value as string[]) ?? [];
        if (values.length > 0) patch.interestTypes = values;
        break;
      }
      case 'createdAt': {
        const [start, end] = (filter.value as [
          string | null,
          string | null,
        ]) ?? [null, null];
        if (start) patch.createdAtStart = start;
        if (end) patch.createdAtEnd = end;
        break;
      }
      case 'updatedAt': {
        const [start, end] = (filter.value as [
          string | null,
          string | null,
        ]) ?? [null, null];
        if (start) patch.updatedAtStart = start;
        if (end) patch.updatedAtEnd = end;
        break;
      }
    }
  }
  return patch;
}

// ─────────────────────────────────────
// Verification helpers
// ─────────────────────────────────────

/** Identity used when the atom map has no entry for a given key yet. */
export const DEFAULT_VERIFICATION_STATE: VerificationState = {
  status: null,
  commentsCount: 0,
};

/**
 * Centralized key builder for `verificationStatesAtom`. Inline
 * `${id}-${type}` template strings drift easily — route every lookup
 * through here so cell and rollup readers can't disagree on shape.
 *
 * @param {number} nriCalculationId - Calculation id from the BE shape
 * @param {VerificationType} type - 'nri' or 'wi'
 * @return {string} Atom-map key
 */
export const verificationKey = (
  nriCalculationId: number,
  type: VerificationType
): string => `${nriCalculationId}-${type}`;

/**
 * Picks the matching `verifications.{nri|workingInterest}` slot from a
 * subtract — centralizes the `type` → field mapping so callers don't
 * inline the ternary.
 *
 * @param {InterestBreakdown} subtract - Subtract entry
 * @param {VerificationType} type - 'nri' or 'wi'
 * @return {InterestBreakdownVerification} The selected verification record
 */
export const getSubtractVerification = (
  subtract: InterestBreakdown,
  type: VerificationType
): InterestBreakdownVerification => {
  if (type === 'nri') return subtract.verifications.nri;
  return subtract.verifications.workingInterest;
};

/**
 * Merges BE-supplied verification state from `rows` onto the previous
 * atom map. Returns `current` (same reference) when no value differs —
 * Jotai then treats the write as a no-op, sparing every subscriber an
 * unnecessary re-render on stable refetches.
 *
 * @param {Record<string, VerificationState>} current - Previous map
 * @param {FlatSlotRow[]} rows - Rows from the slot fetch
 * @return {Record<string, VerificationState>} Merged map (or `current` unchanged)
 */
export const buildHydratedStates = (
  current: Record<string, VerificationState>,
  rows: FlatSlotRow[]
): Record<string, VerificationState> => {
  let next = current;
  const writeIfDifferent = (
    key: string,
    status: boolean | null,
    commentsCount: number
  ) => {
    const existing = next[key];
    if (
      existing &&
      existing.status === status &&
      existing.commentsCount === commentsCount
    )
      return;
    if (next === current) next = { ...current };
    next[key] = { status, commentsCount };
  };
  for (const r of rows) {
    for (const b of r.subtracts ?? []) {
      if (b.nriCalculationId === null) continue;
      writeIfDifferent(
        verificationKey(b.nriCalculationId, 'nri'),
        b.verifications.nri.status,
        b.verifications.nri.commentsCount
      );
      writeIfDifferent(
        verificationKey(b.nriCalculationId, 'wi'),
        b.verifications.workingInterest.status,
        b.verifications.workingInterest.commentsCount
      );
    }
  }
  return next;
};

// ─────────────────────────────────────
// Edit-mode bootstrap helpers
// ─────────────────────────────────────

/**
 * Single source of truth for the draft's Pusher channel name. The BE
 * format lives in `Packages::Draft#pusher_channel` (`app/models/packages/
 * draft.rb`) — kept in lockstep here. A divergence would silently break
 * every materializer-await flow (FE subscribes to a dead channel, await
 * times out). Asserted by both
 * `spec/models/packages/draft_pusher_channel_spec.rb` and
 * `app/javascript/pages/packages/new/__tests__/draft-pusher-channel.test.ts`.
 *
 * @param {string} draftId - Draft UUID
 * @return {string} Pusher channel name
 */
export const draftPusherChannel = (draftId: string): string =>
  `package_draft-${draftId}`;

/**
 * Returns the canonical id string for a {@link DraftAssetRow}: `salesforceId`
 * for tract rows, stringified bigint `id` for non-tract rows. The two fields
 * are mutually exclusive on the wire, so a simple coalesce works. This is the
 * value the BE PATCH `/packages/drafts/:id/assets` accepts in
 * `addedAssetIds` / `removedAssetIds` — type-blind by design (per INS-9030,
 * `draft_slot_allocations.asset_id` is a string column).
 *
 * @param {DraftAssetRow} row - The row whose canonical id we need
 * @return {string} The id string used in PATCH payloads and as the table row key
 */
export const draftAssetRowId = (row: DraftAssetRow): string =>
  row.salesforceId ?? String(row.id ?? '');

/** How much of an asset row a draft holds: all of it, some of a Tract's Subtracts, or none. */
export type DraftAssetHolding = 'whole' | 'part' | 'none';

/**
 * How much of a Step 1 / Step 2 asset row the draft holds. A Tract with more
 * than one Subtract reads it from its Subtract rows' `selected` (all, some or
 * none of them); any other row from its own `selected`.
 *
 * @param {DraftAssetRow} row - An asset row, its Subtract rows as shown
 * @return {DraftAssetHolding} The holding
 */
export const draftAssetHolding = (row: DraftAssetRow): DraftAssetHolding => {
  if (row.subtracts.length < 2) return row.selected ? 'whole' : 'none';
  const held = row.subtracts.filter((subtract) => subtract.selected).length;
  if (held === 0) return 'none';
  return held === row.subtracts.length ? 'whole' : 'part';
};

/**
 * A Step 1 table's first read: the first page at the default size, sorted on
 * `sortId` ascending, nothing searched, filtered or ticked.
 *
 * @param {string} sortId - The column it sorts on
 * @return {TableState} The table's state
 */
export const firstTableState = (sortId: string): TableState => ({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  globalFilter: '',
  sorting: [{ id: sortId, desc: false }],
  columnFilters: [],
  selection: { selectAll: false, toggledNodes: [] },
  expanded: {},
});

/**
 * A preview table, not a workspace: every table feature off, so no toolbar renders. The
 * asset-list results' Matched Assets, and Step 1's Subtract rows under their Tract.
 */
export const PREVIEW_TABLE_CONFIG: DataTableConfig = {
  globalSearch: false,
  columnFilters: false,
  columnVisibility: false,
  export: false,
  sorting: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  hideFooter: true,
};

/**
 * The row ids of the Tracts a draft holds in part among `rows` (`draftAssetHolding`): the rows
 * Step 1 shows partly checked and opens expanded.
 *
 * @param {DraftAssetRow[]} rows - Asset rows, their Subtract rows as shown
 * @return {string[]} Their ids
 */
export const heldInPartRowIds = (rows: DraftAssetRow[]): string[] =>
  rows.filter((row) => draftAssetHolding(row) === 'part').map(draftAssetRowId);

/**
 * Default timeout for awaiting materialization. See
 * {@link subscribeForDraftMaterialization}.
 *
 * Five minutes, not one. The work itself runs 44-48s for a 170-asset union,
 * so a 60s ceiling left almost no room — and the job waits for a worker
 * thread before it starts any work at all. Its tier shares a pool with
 * whatever else is queued there, so a backlog on the burst sub-queue can push
 * the wait well past the work: sandbox has been seen with ~2,500 jobs and 20s
 * of latency on that pool while the materializer's own queue was empty.
 *
 * This ceiling exists to report a draft that is genuinely stuck, not to
 * police how busy the workers are, and a draft that outlives it is no longer
 * a dead end — the retry re-awaits the draft it already wrote.
 */
const DEFAULT_MATERIALIZATION_TIMEOUT_MS = 300_000;

/**
 * Interval between fallback draft-status checks while awaiting
 * materialization. See {@link subscribeForDraftMaterialization} — the
 * poll is a recovery path for missed Pusher events on a one-shot job
 * signal, not a data-refetch loop; Pusher remains the fast path.
 *
 * Cost note: each check renders the full draft payload (asset/slot
 * summaries + id arrays), so the worst case is ~60 GETs across the 300s
 * wait — five times what it was at the old 60s ceiling, and the reason
 * to reach for a lightweight status-only endpoint before widening the
 * ceiling further. Only a wait that runs its full length pays it; the
 * Pusher event settles the normal case long before.
 */
const DRAFT_STATUS_POLL_INTERVAL_MS = 5_000;

/**
 * Grace window for the last-chance status check at the timeout ceiling.
 * Keeps the ceiling HARD: a hanging final GET must not leave `wait()`
 * pending forever, so the rejection races the check and fires at
 * `timeoutMs + FINAL_CHECK_GRACE_MS` at the latest.
 */
const FINAL_CHECK_GRACE_MS = 3_000;

/** Watcher returned by {@link subscribeForDraftMaterialization}. */
export interface DraftMaterializationWatcher {
  /**
   * Resolves on `status: 'ready'`, rejects on `'failed'` or after the
   * timeout ceiling (`timeoutMs`, plus a short grace for one final
   * status check). Calling `wait()` after the event has already fired
   * (because we subscribed before triggering the work) resolves
   * immediately. When `draftId` was provided, calling `wait()` also
   * arms the status-check fallback — a one-shot
   * `GET /packages/drafts/:id` plus an interval re-check — so a missed
   * Pusher event is recovered from the persisted draft status.
   * Idempotent: repeat calls return the same promise.
   */
  wait(): Promise<void>;
  /**
   * Always-safe cleanup. Idempotent. Should be called in `finally`.
   * Tears down the Pusher subscription, clears the timeout and the
   * status-check interval, and aborts any in-flight status request.
   * A `wait()` still pending when `cancel()` runs is rejected with a
   * cancellation error.
   */
  cancel(): void;
}

/**
 * Subscribe to a draft's Pusher channel BEFORE triggering the work
 * that causes a `status_change` event. The returned watcher's `wait()`
 * may be called (or skipped) once the caller knows whether
 * materialization happened.
 *
 * Three hazards this closes:
 *   1. **Subscribe-after-event**: the materializer fires the event in
 *      <100ms (warm Sidekiq), faster than the caller can `await
 *      patch()`-then-`subscribe()`. Pusher does not replay missed
 *      events. We subscribe first so the binding is in place before
 *      the trigger.
 *   2. **Permanent UI lock**: if the event never arrives (worker
 *      stall, channel-name mismatch, network hiccup), the wait would
 *      otherwise hang forever. The timeout rejects with a clear error
 *      so the caller can re-enable interaction and surface a message.
 *   3. **Missed or late event** (the ONCALL "materializer may have
 *      stalled" reports): on `POST /packages/drafts` the channel name
 *      only exists after the 202, so subscribing before the trigger is
 *      impossible — a fast materializer can emit `status_change` before
 *      the websocket subscription is live and the event is lost. The
 *      PATCH flows subscribe first but still have the subscription
 *      handshake gap. When `draftId` is provided, `wait()` recovers
 *      from the draft's DB status: a one-shot `GET /packages/drafts/:id`
 *      immediately, an interval re-check while waiting, and a final
 *      check at the timeout ceiling. Pusher stays the fast path; the
 *      poll is a best-effort job-status recovery, not a data-refetch
 *      loop — checks never reject the wait on network errors.
 *
 * @param {string} channel - Pusher channel name; use {@link draftPusherChannel}.
 * @param {object} [opts] - Options
 * @param {string} [opts.draftId] - Draft UUID enabling the status-poll
 *   fallback. Callers that trigger materialization AFTER constructing
 *   the watcher get correct results too: the fallback only starts at
 *   `wait()`, which those callers invoke after their PATCH has already
 *   reset the draft to `materializing`.
 * @param {number} [opts.timeoutMs] - Override the default 300s timeout.
 * @return {DraftMaterializationWatcher} Watcher with wait() and cancel()
 */
export const subscribeForDraftMaterialization = (
  channel: string,
  {
    draftId,
    timeoutMs = DEFAULT_MATERIALIZATION_TIMEOUT_MS,
  }: { draftId?: string; timeoutMs?: number } = {}
): DraftMaterializationWatcher => {
  let settled = false;
  // Captured event before `wait()` is called — so a fast materializer
  // that fires before the caller awaits doesn't get dropped.
  let pendingResolution:
    { kind: 'ready' } | { kind: 'failed'; error: Error } | null = null;
  let resolveFn: (() => void) | null = null;
  let rejectFn: ((e: Error) => void) | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let pollId: ReturnType<typeof setInterval> | null = null;
  let waitPromise: Promise<void> | null = null;
  let statusCheckInFlight = false;
  const statusAbortController = new AbortController();

  /**
   * Clears the timeout and the status-check interval. Safe when neither
   * has been armed yet.
   *
   * @return {void}
   */
  const clearTimers = () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (pollId) clearInterval(pollId);
  };

  /**
   * Single settlement path shared by the Pusher handler, the status-check
   * fallback, and the timeout. First writer wins; later callers no-op.
   * When `wait()` hasn't been called yet the resolution is stashed on
   * `pendingResolution` and replayed on the next `wait()`.
   *
   * @param {object} resolution - `{ kind: 'ready' }` or `{ kind: 'failed', error }`
   * @return {void}
   */
  const settle = (
    resolution: { kind: 'ready' } | { kind: 'failed'; error: Error }
  ) => {
    if (settled) return;
    settled = true;
    clearTimers();
    if (resolveFn) {
      if (resolution.kind === 'ready') resolveFn();
      else rejectFn?.(resolution.error);
    } else {
      // Settled before wait() was called — stash for later.
      pendingResolution = resolution;
    }
  };

  const subscription = subscribe(channel);

  /**
   * Pusher `status_change` handler — the fast path. Ignores non-terminal
   * statuses; terminal ones settle the wait.
   *
   * @param {DraftStatusChangeEvent} event - Payload from the materializer
   * @return {void}
   */
  const handler = ({ status, failureReason }: DraftStatusChangeEvent) => {
    if (status !== 'ready' && status !== 'failed') return;
    settle(
      status === 'ready'
        ? { kind: 'ready' }
        : {
            kind: 'failed',
            error: new Error(failureReason ?? 'Draft materialization failed'),
          }
    );
  };
  subscription.bind('status_change', handler);

  /**
   * Best-effort recovery check: reads the draft's persisted status and
   * settles the wait when the job already reached a terminal state.
   * No-ops when `draftId` is absent, when already settled, when a check
   * is still in flight, and on any transport error — a 5xx/network blip
   * can never fail the wait (the Pusher binding and later checks stay
   * live), so this can only ever help.
   *
   * @return {Promise<void>} Resolves once the check was applied or skipped
   */
  const checkDraftStatus = async (): Promise<void> => {
    if (!draftId || settled || statusCheckInFlight) return;
    statusCheckInFlight = true;
    try {
      const draft = await request.get<DraftResponse>(
        `/packages/drafts/${draftId}`,
        statusAbortController
      );
      if (settled || typeof draft !== 'object' || draft === null) return;
      if ('error' in draft) return;
      if (draft.status === 'ready') {
        settle({ kind: 'ready' });
      } else if (draft.status === 'failed') {
        settle({
          kind: 'failed',
          error: new Error(
            draft.failureReason ?? 'Draft materialization failed'
          ),
        });
      }
    } finally {
      statusCheckInFlight = false;
    }
  };

  return {
    wait: () => {
      if (waitPromise) return waitPromise;
      waitPromise = new Promise<void>((resolve, reject) => {
        if (pendingResolution) {
          if (pendingResolution.kind === 'ready') resolve();
          else reject(pendingResolution.error);
          return;
        }
        if (settled) return resolve();
        resolveFn = resolve;
        rejectFn = reject;
        if (draftId) {
          // One-shot immediately (closes the subscribe-after-event race:
          // if the job already finished, the DB says so), then re-check
          // on an interval until settled.
          checkDraftStatus().catch(() => {});
          pollId = setInterval(() => {
            checkDraftStatus().catch(() => {});
          }, DRAFT_STATUS_POLL_INTERVAL_MS);
        }
        timeoutId = setTimeout(() => {
          if (settled) return;
          // Last-chance recovery before failing: the event may simply
          // have been missed while the job succeeded. Raced against a
          // short grace timer so a HANGING request cannot leave the
          // wait pending forever — the ceiling stays hard.
          void Promise.race([
            checkDraftStatus().catch(() => {}),
            new Promise<void>((graceResolve) => {
              setTimeout(graceResolve, FINAL_CHECK_GRACE_MS);
            }),
          ]).finally(() => {
            settle({
              kind: 'failed',
              error: new DraftMaterializationTimeout(
                'Timed out waiting for the draft to be ready. Try again.'
              ),
            });
          });
        }, timeoutMs);
      });
      return waitPromise;
    },
    cancel: () => {
      if (!settled) {
        settled = true;
        clearTimers();
        // A pending wait() must not hang forever after teardown — reject
        // it so awaiting callers (whose cleanup guards already swallow
        // this) can finish. Callers that cancel after settlement (the
        // normal `finally` pattern) never reach this branch.
        rejectFn?.(new Error('Draft materialization wait cancelled'));
      }
      clearTimers();
      statusAbortController.abort();
      subscription.unbind('status_change', handler);
      subscription.unsubscribe();
    },
  };
};

/**
 * The wait for a draft's build ran out before the build reported ready or
 * failed. Unlike a failure, the build may still be running.
 */
export class DraftMaterializationTimeout extends Error {}

/**
 * Convenience wrapper: subscribe + wait in one call. Used by initial
 * draft creation where there's no caller-managed lifecycle.
 * Per-call subscription; cleans up its own listener and channel handle.
 *
 * Create flows can only subscribe AFTER the 202 hands back the channel
 * name — a fast materializer may already have emitted the event by
 * then — so `draftId` is REQUIRED here: it arms the status-check
 * fallback that recovers the missed event from the draft's persisted
 * status (see {@link subscribeForDraftMaterialization}).
 *
 * @param {string} channel - Pusher channel from `DraftResponse.pusherChannel`
 * @param {string} draftId - Draft UUID for the status-check fallback
 * @return {Promise<void>} Resolves on `status: 'ready'`, rejects on `'failed'` or timeout
 */
export const waitForDraftMaterialization = (
  channel: string,
  draftId: string
): Promise<void> => {
  const watcher = subscribeForDraftMaterialization(channel, { draftId });
  return watcher.wait().finally(() => watcher.cancel());
};

/** A create or update draft the server refused with reasons (any error answer carrying `errors`). */
export class DraftRefusal extends Error {}

/**
 * A create or update draft the server refused, writing nothing, because the
 * Assets table would be above its warning size: the user confirms, and the same
 * scope is sent again with `confirmLargeTable`.
 */
export class LargeTableRefusal extends DraftRefusal {
  /** How many assets the table would hold. */
  tableAssetCount: number;

  /**
   * @param {string} message - The server's reason
   * @param {number} tableAssetCount - How many assets the table would hold
   */
  constructor(message: string, tableAssetCount: number) {
    super(message);
    this.tableAssetCount = tableAssetCount;
  }
}

/**
 * The draft's refusal as the error the wizard shows: a `LargeTableRefusal` when
 * the server gave the table's size, which only a large-table refusal carries.
 * A refusal with no reasons says the save failed.
 *
 * @param {DraftRefusalBody} body - The 422 body
 * @return {DraftRefusal} The error to throw
 */
export const draftRefusal = ({
  errors,
  tableAssetCount,
}: DraftRefusalBody): DraftRefusal => {
  const message = (errors ?? []).join(', ') || 'Failed to save draft';
  return tableAssetCount === undefined
    ? new DraftRefusal(message)
    : new LargeTableRefusal(message, tableAssetCount);
};

/**
 * The scope Next on Select Source sends for the form, or null with nothing to
 * send: no source or date, no matched name on the asset list, nothing picked
 * on the other sources. Map Query and Existing Package carry the list beside
 * them (`[]` with none).
 *
 * @param {PackageFormValues} v - Current form values
 * @param {string[]} listed - The asset list's matched names, in list order
 * @return {ScopePayload | null} The scope
 */
export const step0Scope = (
  v: PackageFormValues,
  listed: string[]
): ScopePayload | null => {
  const { sourceType, sources, asOfDate } = v;
  if (sourceType === null || asOfDate === null) return null;
  if (sourceType === 'assetList') {
    return listed.length > 0
      ? { asOfDate, sourceType, assetNames: listed }
      : null;
  }
  if (sources.length === 0) return null;
  if (sourceType === 'mapQuery') {
    return {
      asOfDate,
      sourceType,
      mapQuery: mapQueryFilterParams(sources, v.mapQuery),
      assetNames: listed,
    };
  }
  return {
    asOfDate,
    sourceType,
    sourceIds: sources.map(({ id }) => id),
    ...(sourceType === 'package' ? { assetNames: listed } : {}),
  };
};

/**
 * The list beside a primary source, as a draft body field: absent with no
 * matched name, which on an update drops a list the draft had.
 *
 * @param {string[]} [assetNames] - The list's matched names
 * @return {object} `{ assetNames }`, or nothing
 */
const listBeside = (assetNames: string[] = []) =>
  assetNames.length > 0 ? { assetNames } : {};

/**
 * Map a Step 0 scope to the `POST /packages/drafts` body. The BE Creator
 * requires both `asOfDate` and exactly one base source, with a list beside Map
 * Query or Existing Package.
 *
 * @param {ScopePayload} scope - Form's source + asOfDate
 * @return {CreateDraftPayload} Body for create
 */
export const scopeToCreateDraftPayload = (
  scope: ScopePayload
): CreateDraftPayload => {
  if (scope.sourceType === 'assetList') {
    return { asOfDate: scope.asOfDate, assetNames: scope.assetNames };
  }
  if (scope.sourceType === 'mapQuery') {
    return {
      asOfDate: scope.asOfDate,
      mapQuery: scope.mapQuery,
      ...listBeside(scope.assetNames),
    };
  }
  if (scope.sourceType === 'entity') {
    return { asOfDate: scope.asOfDate, sellerEntityIds: scope.sourceIds };
  }
  return {
    asOfDate: scope.asOfDate,
    packageIds: scope.sourceIds,
    ...listBeside(scope.assetNames),
  };
};

/**
 * The source half of a `PATCH /packages/drafts/:id` body. Always the WHOLE
 * selection: the BE replaces the side it is given rather than diffing it, and
 * the sources are exclusive, so naming one clears the others. Map Query and
 * Existing Package carry the list beside them: the BE drops a list a primary
 * comes without, and applies a change of the list alone, beside an unchanged
 * primary, incrementally. Editing a package stacks this on the draft's
 * `sourcePackageId`, which is fixed at creation and unaffected either way.
 *
 * @param {ScopePayload} scope - Form's source + asOfDate
 * @return {Partial<UpdateDraftPayload>} Source fragment for the PATCH body
 */
export const scopeToUpdateDraftPayload = (
  scope: ScopePayload
): Partial<UpdateDraftPayload> => {
  if (scope.sourceType === 'assetList') {
    return { assetNames: scope.assetNames };
  }
  if (scope.sourceType === 'mapQuery') {
    return { mapQuery: scope.mapQuery, ...listBeside(scope.assetNames) };
  }
  if (scope.sourceType === 'entity') {
    return { sellerEntityIds: scope.sourceIds };
  }
  return { packageIds: scope.sourceIds, ...listBeside(scope.assetNames) };
};

/** Step 0 form fields that contribute to the draft PATCH body. */
export const STEP_0_FIELDS: readonly (keyof PackageFormValues)[] = [
  'sources',
  'assetList',
  'mapQuery',
  'asOfDate',
];

/** The draft request a Next on Select Source sends, if any. */
export type Step0DraftWrite =
  | { kind: 'none' }
  | { kind: 'create'; payload: CreateDraftPayload }
  | { kind: 'update'; draftId: string; payload: UpdateDraftPayload };

/**
 * A draft's last write that landed: the scope it sent, and how the last wait
 * for its build ended when the draft did not come out ready. A timeout leaves
 * the build possibly still running; a failure leaves nothing built.
 */
export interface LastDraftWrite {
  scope: ScopePayload;
  unbuilt: 'timedOut' | 'failed' | null;
}

/**
 * What Next on Select Source sends for `scope`, and which Step 0 fields it
 * syncs (to mark clean once the write lands).
 *
 * POST, with the full scope, when there is no draft yet. Otherwise a PATCH of
 * the Step 0 fields changed since the last sync — `isDirty` tracks "since the
 * last successful sync" because the caller clears it after each one — or
 * nothing when none changed, so a re-Next keeps Step 1's choices. A change of
 * the date alone sends the date (a Map Query draft re-runs its stored filters);
 * any other change sends the whole source (with the list beside it). On the
 * asset list that PATCH carries the whole list, as-of date change or not: the
 * BE re-applies any list it is sent, carrying no Step 1 choice.
 *
 * When the same scope was last sent (source ids compared as sets, as the BE
 * compares them), nothing is sent, even with fields left dirty by an edit
 * changed back, and Next waits on that build (at once when it is ready, again
 * when the wait timed out and it may still be running) rather than start
 * another and discard Step 1's choices. The one exception is a failed build of
 * a scope with a list (the Asset List source, or a list beside Map Query or
 * Existing Package): it is sent again, which is how it is retried, as the BE
 * rebuilds from any list it is sent. The BE refuses an unchanged table source
 * or Map Query with no list, so those have no such retry, and Next reports the
 * failed build again.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {ScopePayload} scope - The Step 0 scope
 * @param {LastDraftWrite|null} lastWrite - This draft's last write, if any
 * @return {Object} The request and the fields it syncs
 */
export const step0DraftWrite = (
  form: PackageFormInstance,
  scope: ScopePayload,
  lastWrite: LastDraftWrite | null = null
): { write: Step0DraftWrite; dirty: (keyof PackageFormValues)[] } => {
  const dirty = dirtyFieldNames(form).filter((name) =>
    STEP_0_FIELDS.includes(name)
  );
  const { draftId } = form.state.values;
  if (!draftId) {
    return {
      dirty,
      write: { kind: 'create', payload: scopeToCreateDraftPayload(scope) },
    };
  }
  const onList = scope.sourceType === 'assetList';
  const listed = 'assetNames' in scope && (scope.assetNames?.length ?? 0) > 0;
  const retry = listed && lastWrite?.unbuilt === 'failed';
  if (
    lastWrite &&
    !retry &&
    isEqual(idsAsSets(scope), idsAsSets(lastWrite.scope))
  ) {
    return { dirty, write: { kind: 'none' } };
  }
  if (dirty.length === 0 && !retry) return { dirty, write: { kind: 'none' } };

  const payload: UpdateDraftPayload = {};
  if (dirty.includes('asOfDate')) payload.asOfDate = scope.asOfDate;
  // Always the WHOLE source: a table field, the Map Query filters or the list
  // changing means the source in play is sent, never a table side emptied by a
  // tile flip.
  if (onList || retry || dirty.some((name) => name !== 'asOfDate')) {
    Object.assign(payload, scopeToUpdateDraftPayload(scope));
  }
  return { dirty, write: { kind: 'update', draftId, payload } };
};

/**
 * A scope with its source ids and its list's names in order, so a scope ticked
 * or a list pasted in another order compares equal: the BE compares the ids as
 * sets, and the same names in any order match the same assets.
 *
 * @param {ScopePayload} scope - A Step 0 scope
 * @return {ScopePayload} The same scope, ids and names sorted
 */
const idsAsSets = (scope: ScopePayload): ScopePayload => {
  const sorted =
    'sourceIds' in scope
      ? { ...scope, sourceIds: [...scope.sourceIds].sort((a, b) => a - b) }
      : scope;
  return sorted.assetNames
    ? { ...sorted, assetNames: [...sorted.assetNames].sort() }
    : sorted;
};

/**
 * Names of every form field whose `isDirty` meta flag is currently true.
 * Used by Step 0 to filter against `STEP_0_FIELDS` and decide whether
 * to fire the draft PATCH (and which fields to clear via `setFieldMeta`
 * after a successful sync).
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {Array} Dirty field keys
 */
export const dirtyFieldNames = (
  form: PackageFormInstance
): Array<keyof PackageFormValues> =>
  (Object.keys(form.state.fieldMeta) as Array<keyof PackageFormValues>).filter(
    (name) => form.state.fieldMeta[name]?.isDirty
  );

/**
 * Names of every form field whose current value deep-differs from the
 * form's mount-time `defaultValues`. `isDefaultValue` is TanStack-derived
 * (recomputed on every change) and self-cleans when the user reverts a
 * field to its baseline. Used by Step 2 to build the package PATCH body
 * against the persisted package state — typing "Acme" → "Foo" → "Acme"
 * produces no diff entry.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {Array} Field names that diverge from defaults
 */
export const divergedFieldNames = (
  form: PackageFormInstance
): Array<keyof PackageFormValues> =>
  (Object.keys(form.state.fieldMeta) as Array<keyof PackageFormValues>).filter(
    (name) => form.state.fieldMeta[name]?.isDefaultValue === false
  );

/**
 * Flip `draftId.isTouched = true`. Hijacked semantics: this flag means
 * "draft has diverged from the persisted package" — Step 2 save reads
 * it to decide whether to include `draft_id` in the wire body (which
 * triggers the BE wizard branch's selection sync).
 *
 * Called by source / asOfDate handlers in Step 0 and by asset / slot
 * mutation success branches in Step 1. Idempotent — already-touched is
 * a no-op so back-to-back asset / slot toggles don't fire redundant
 * store notifications.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {void}
 */
export const markDraftTouched = (form: PackageFormInstance): void => {
  if (form.state.fieldMeta.draftId?.isTouched) return;
  form.setFieldMeta('draftId', (m) => ({ ...m, isTouched: true }));
};

/**
 * Every seller of the package that resolved to an Insights entity, as Step 0
 * source rows — the edit-mode hydration of the entity path. A seller with no
 * entity behind it (`id: null`) has nothing to select, so it's skipped; a
 * package with only those hydrates to `[]` and Step 0 shows no selection.
 * Edit mode is wizard-packages-only (`type === ''`), so in practice every
 * seller here is a `package_seller_entities` row.
 *
 * @param {PackageSeller[]} sellers - Sellers from GET /packages/:id
 * @return {SourceRow[]} One row per entity-backed seller, in seller order
 */
export const sourcesFromSellers = (sellers: PackageSeller[]): SourceRow[] =>
  entitySellers(sellers).map((seller) => ({
    id: seller.id,
    name: seller.name,
    href: seller.href,
  }));

/** A seller that has an Insights entity behind it (linked variant). */
export type EntitySeller = Extract<PackageSeller, { id: number }>;

/**
 * Edit-mode PATCH fragments keyed by form field, merged into the wire body
 * only for the fields that diverged from the persisted package.
 *
 * `Packages::Updater` reads both entity-id lists by KEY PRESENCE: omitting the
 * key leaves the join table alone, sending `[]` deletes every row in it.
 *
 *   - sellers: only an entity source names sellers, and only a non-empty
 *     selection, so the fragment is empty (= key omitted) otherwise. A package
 *     source, no source, or a cleared selection must leave the persisted set
 *     alone — the wizard must not silently wipe a multi-seller package.
 *   - acquirers: the field is what Step 2 shows, so it is sent as is — `[]`
 *     when the selected source packages name none. A tile flip restores the
 *     persisted set rather than emptying it (see {@link baselineAcquirers}),
 *     so an empty list here is always one the user can see.
 *
 * @param {PackageFormValues} values - Current form values
 * @return {Object} Partial wire-body fragments keyed by form field
 */
export const editPackageFragments = (
  values: PackageFormValues
): Partial<Record<keyof PackageFormValues, Partial<SavePackagePayload>>> => ({
  packageName: { name: values.packageName.trim() },
  acquiringEntities: {
    acquirer_entity_ids: values.acquiringEntities.map((entity) => entity.id),
  },
  effectiveDate: { effective_date: values.effectiveDate },
  psaDate: { psa_date: values.psaDate },
  closeDate: { close_date: values.closeDate },
  asOfDate: { as_of_date: values.asOfDate },
  sources:
    values.sourceType === 'entity' && values.sources.length > 0
      ? { seller_entity_ids: values.sources.map((source) => source.id) }
      : {},
});

/**
 * The edit-mode PATCH body: the {@link editPackageFragments} of every field
 * whose value differs from the form's mount-time defaults (the persisted
 * package), via TanStack's self-cleaning `isDefaultValue`, plus `draft_id`
 * when the draft was touched — that covers selection / scope changes that
 * don't show up in form fields.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {Partial<SavePackagePayload>} Wire body; empty when nothing changed
 */
export const editPackageBody = (
  form: PackageFormInstance
): Partial<SavePackagePayload> => {
  const values = form.state.values;
  const fragments = editPackageFragments(values);
  const body: Partial<SavePackagePayload> = {};
  for (const name of divergedFieldNames(form)) {
    Object.assign(body, fragments[name]);
  }
  if (form.state.fieldMeta.draftId?.isTouched && values.draftId !== null) {
    body.draft_id = values.draftId;
  }
  return body;
};

/**
 * Every seller of the package that resolved to an Insights entity; one that
 * didn't (`id: null`) is skipped. Shared by edit-mode hydration and the
 * wizard's package-source pick so both resolve the same entities for the Add
 * Wellbore / Add LTD modals.
 *
 * A legacy package's seller resolves to its CRM account's entity only when
 * exactly one is linked, so a legacy source is skipped here both when its CRM
 * account links to no entity and when it links to several — while
 * `Packages::Creator` still inherits all of them in the second case. The two
 * arrive identically (`id: null`), so a source dropped for the second reason
 * cannot be told from one dropped for the first, and
 * {@link entityOptionsFromSources} offers what it can resolve rather than
 * blocking on the possibility. Distinguishing them needs a signal the
 * packages API does not send.
 *
 * @param {PackageSeller[]} sellers - Sellers as returned by the packages API
 * @return {EntitySeller[]} Entity-backed sellers, in the given order
 */
export const entitySellers = (sellers: PackageSeller[]): EntitySeller[] =>
  sellers.filter((seller): seller is EntitySeller => seller.id !== null);

/**
 * Assemble the form's `defaultValues` from a `GET /packages/:id` response
 * + the materialized draft id. The selection hydrates from every
 * entity-backed seller of the package (not the original Step 0 pick, which
 * could have been another package) and is empty when there is none —
 * `sourceType` then stays null so neither tile looks pre-picked. Step 0's
 * gate accepts that untouched empty state (see {@link canAdvanceStep0}), so a
 * sellerless package doesn't block navigation. If the user wants to flip
 * the draft's scope they pick a tile + rows on Step 0 and the upsert PATCHes
 * the BE.
 *
 * @param {EditPackageDetail} pkg - GET /packages/:id response
 * @param {string} draftId - Materialized draft id from the bootstrap POST
 * @param {number|null} draftScenarioId - The draft's Type Curve Scenario, inherited from the
 *   source package by the BE Creator. Seeds Step 1's picker so re-opening a package shows the
 *   scenario its slots were actually built from.
 * @return {PackageFormValues} Hydrated form defaults for edit mode
 */
export const packageDetailToFormValues = (
  pkg: EditPackageDetail,
  draftId: string,
  draftScenarioId: number | null = null
): PackageFormValues => {
  const sources = sourcesFromSellers(pkg.sellers);
  return {
    sourceType: sources.length > 0 ? 'entity' : null,
    sources,
    assetList: EMPTY_ASSET_LIST,
    mapQuery: EMPTY_MAP_QUERY_FILTERS,
    asOfDate: pkg.asOfDate,
    draftId,
    // Seeded from the DRAFT, not the package: Packages::Drafts::Creator inherits the source
    // package's scenario, and the draft is what Step 1 actually acts on.
    typeCurveScenarioId: draftScenarioId,

    packageName: pkg.name,
    acquiringEntities: pkg.acquirerEntities.map(packageAcquirerToFormEntity),
    effectiveDate: pkg.effectiveDate,
    psaDate: pkg.psaDate,
    closeDate: pkg.closeDate,
  };
};

/**
 * Map a draft-slots response into the slot-grain rows the Step 2 "Slot
 * Details" review table renders. Mirrors the shape of
 * `mapSlotNRIResponseToSlotNRI` (`utils/misc.ts:275`) — one output row
 * per input slot, with select tract metadata pulled from `tracts[0]`.
 *
 * Fields the BE doesn't emit yet (see `Step2SlotRow` TODOs) are filled
 * with `null` so the column layout stays final and only the cell
 * content changes when those fields land.
 *
 * @param {DraftSlot[]} slots - Slots from `POST /queries/packages/drafts/:id/slots`
 * @return {Step2SlotRow[]} One slot-grain row per input slot
 */
export const mapDraftSlotsToStep2SlotRows = (
  slots: DraftSlot[]
): Step2SlotRow[] =>
  slots.map((slot) => {
    const firstTract = slot.tracts[0];
    return {
      id: slot.slotId,
      slotId: slot.uwi ?? slot.slotId,
      uwi: slot.uwi,
      unitName: slot.unitName,
      unitHref: slot.unitHref,
      totalSlotNri: slot.nri,
      totalSlotWi: slot.workingInterest,
      wiSource: slot.wiSource ?? undefined,
      nriCertifiedSource: slot.nriCertifiedSource ?? undefined,
      nriCertifiedWorkspaceId: slot.nriCertifiedWorkspaceId ?? undefined,
      wiVerified: null,
      interestType: slot.interestType ?? firstTract?.interestType ?? null,
      unitSource: null,
      unitDevelopmentType: null,
      wellName: slot.wellName,
      wellStatus: slot.wellStatus,
      typeCurveStatus: null,
      operator: slot.operator,
      holeDirection: slot.holeDirection,
      formation: slot.formation,
      slotNumber: slot.slotNumber,
      permitDate: slot.wellPermitDate,
      spudDate: slot.wellSpudDate,
      rigReleaseDate: null,
      completionDate: null,
      firstProductionDate: slot.wellFirstProductionDate,
    };
  });

/**
 * Returns true when the row's asset type is editable via the overflow menu.
 * Tract rows are not editable here (INS-9066): they round-trip through the
 * existing CRM Tract wizard, not this overflow menu.
 *
 * @param {string} assetType - The row's asset type discriminator
 * @return {boolean} True if the row should show an Edit overflow menu item
 */
export function canEditRow(assetType: FlatSlotRow['assetType']): boolean {
  return assetType !== 'Tract';
}

/** Step 0 selection slice the effective-entity helpers read. */
type SourceSelection = Pick<PackageFormValues, 'sourceType' | 'sources'> & {
  /** The owning entities of the asset list's matched Tracts. */
  assetListEntities?: NamedAssetListEntity[];
};

/**
 * Every entity the in-flight Add Wellbore / Add LTD modals may file a new
 * asset under. They POST to `/entities/:entity_id/assets` — one entity in the
 * URL path — so when this returns several the modal asks which one, and when
 * it returns one the modal fills it in.
 *
 * - `entity` selection: the selected entities are the sellers.
 * - `mapQuery`: the ticked entities, and the owning entities of the Tracts an
 *   added list matched, unioned by id.
 * - `package` selection: the entity-backed sellers across the selected source
 *   packages, and the owning entities of the Tracts an added list matched,
 *   unioned by id.
 * - `assetList`: the owning entities of the matched Tracts, unioned by id —
 *   the sellers the BE inherits for a list.
 *
 * This is a subset of what the BE `Packages::Creator` inherits at final
 * submit, not a mirror of it: a legacy source whose CRM seller account links
 * to SEVERAL entities contributes all of them on the BE but none here, since
 * the display entry it comes from is unlinked. Note the name of a legacy
 * source's seller is its CRM ACCOUNT's name, so that — not the entity's — is
 * what the entity field offers for a legacy source.
 *
 * @param {SourceSelection} selection - Wizard Step 0 selection
 * @return {EntityRef[]} Entities to offer, or `[]` when none are known
 */
export function entityOptionsFromSources(
  selection: SourceSelection
): EntityRef[] {
  const { sourceType, sources } = selection;
  if (sourceType === 'entity') {
    return sources.map((source) => ({ id: source.id, name: source.name }));
  }
  // The list's owners are sellers beside every source that takes a list, as
  // the BE inherits them at save.
  const listOwners = (selection.assetListEntities ?? []).map(
    ({ id, name }) => ({
      id,
      name,
    })
  );
  if (sourceType === 'assetList') return uniqBy(listOwners, 'id');
  if (sourceType === 'mapQuery') {
    return uniqBy(
      [...sources.map(({ id, name }) => ({ id, name })), ...listOwners],
      'id'
    );
  }
  if (sourceType !== 'package') return [];

  return uniqBy(
    [...sources.flatMap((source) => source.sellers ?? []), ...listOwners],
    'id'
  );
}

/**
 * Acquiring entities to pre-populate Step 2 with, unioned by id across the
 * selected source packages. Mirrors the BE `Packages::Creator` inheritance so
 * the user sees — and can override — the default before submitting.
 *
 * @param {SourceRow[]} sources - Selected Step 0 source rows
 * @return {AcquiringEntity[]} Unioned acquirers
 */
export const acquirersFromSources = (sources: SourceRow[]): AcquiringEntity[] =>
  uniqBy(
    sources.flatMap((source) => source.acquirers ?? []),
    'id'
  );

/**
 * The acquirers the form opened with: the persisted set in edit mode, `[]` when
 * creating. A Step 0 tile flip drops the source packages the acquirers were
 * derived from, so it puts these back — clearing them instead would read as
 * "remove every acquirer" to the edit-mode save.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {AcquiringEntity[]} The mount-time acquirers
 */
export const baselineAcquirers = (
  form: PackageFormInstance
): AcquiringEntity[] => form.options.defaultValues?.acquiringEntities ?? [];

/**
 * Apply a Step 0 selection change to the form: merge the delta into `sources`,
 * re-derive the acquirer pre-population on the package path, and mark the draft
 * touched so the next Next-click re-syncs it.
 *
 * Shared because the selection can change from two places — the source tables
 * and the header's tag set — and all three effects have to happen in both. A
 * header removal that skipped `markDraftTouched` would leave the draft holding
 * a source the user just dropped.
 *
 * A change for the other source kind is dropped. A select-all response can
 * land after the user switched tiles, and applying it then would put package
 * ids into an entity selection (or the reverse).
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {SourceType} type - Which source kind the change came from
 * @param {SourceSelectionChange} change - Rows to add / ids to drop
 * @return {void}
 */
export const applySourceSelection = (
  form: PackageFormInstance,
  type: SourceType,
  change: SourceSelectionChange
): void => {
  if (type !== form.state.values.sourceType) return;
  const next = mergeSourceSelection(form.state.values.sources, change);
  form.setFieldValue('sources', next);
  if (type === 'package') {
    form.setFieldValue('acquiringEntities', acquirersFromSources(next));
  }
  markDraftTouched(form);
};

/**
 * Change Map Query's location ticks and map filter in the form. An update that
 * leaves them as they were changes nothing: TanStack marks a field changed on
 * any write, and Next would then send them.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {Function} update - The filters' next value from their current one
 * @return {void}
 */
export const changeMapQueryFilters = (
  form: PackageFormInstance,
  update: MapQueryFiltersUpdate
): void => {
  const current = form.state.values.mapQuery;
  const next = update(current);
  if (!isEqual(next, current)) form.setFieldValue('mapQuery', next);
};
